import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { ToWords } from "to-words";
import { adToBs } from "@sbmdkl/nepali-date-converter";
import { formatSalesValue } from "../../utils/formatUtils";

const styles = StyleSheet.create({
  page: { padding: 24, width: 700, fontFamily: "Helvetica", fontSize: 10, color: "#000" },
  center: { textAlign: "center" },
  headerLogo: { alignItems: "center", marginBottom: 4 },
  logo: { height: 96 },
  company: { fontSize: 16, fontWeight: "bold", textDecoration: "underline", textDecorationThickness: "2px" },
  smallText: { fontSize: 10, marginTop: 7 },
  sectionTitle: { fontSize: 10, fontWeight: "bold", textDecoration: "underline", marginTop: 6, marginBottom: 4 },
  flexBetween: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", marginVertical: 5 },
  borderLine: { borderBottomWidth: 2, borderTopWidth: 2, borderColor: "#000", height: 5, marginVertical: 2 },
  table: { width: "100%", fontSize: 9 },
  th: { paddingBottom: 2 },
  td: {
    padding: 0,
    lineHeight: 0.2,
  },
  tHeadBorderTop: { borderTopWidth: 2, borderColor: "#000" },
  tHeadBorderBottom: { borderBottomWidth: 2, borderColor: "#000" },
  bold: { fontWeight: "bold" },
  footerLine: { borderBottomWidth: 4, borderColor: "#000", marginTop: 1 },
});

const ExportableInvoicePDF = ({ billInfo, billInfoList, logoSrc }) => {
  const address = localStorage.getItem("selectedAddress");
  const company = localStorage.getItem("selectedCompany");
  const pan_no = localStorage.getItem("selectedPan");
  const phone = localStorage.getItem("selectedPhone");

  const toWords = new ToWords({
    localeCode: "en-IN",
    converterOptions: {
      currency: true,
      ignoreDecimal: false,
      ignoreZeroCurrency: false,
      doNotAddOnly: false,
      currencyOptions: {
        name: "Rupee",
        plural: "Rupees",
        symbol: "₹",
        fractionalUnit: {
          name: "Paisa",
          plural: "Paise",
          symbol: "",
        },
      },
    },
  });

  const taxable_total = billInfoList.reduce((s, i) => s + Number(i.total), 0);
  const round_off = Number(billInfo.total) - Number(taxable_total) - Number(billInfo.vat) + Number(billInfo.DiscountAmt);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.center}>
          <View style={styles.headerLogo}>
            {logoSrc && <Image src={logoSrc} style={styles.logo} />}
          </View>
          <Text style={styles.company}>{company}</Text>
          <View style={styles.smallText}>
            <Text>{address}</Text>
            {pan_no && <Text>PAN No: {pan_no}</Text>}
            {phone && <Text>Phone: {phone}</Text>}
          </View>
        </View>

        <Text style={[styles.sectionTitle, styles.center]}>TAX INVOICE</Text>

        <View style={styles.flexBetween}>
          <View style={styles.smallText}>
            <Text>Bill No: {billInfo.bill_no}</Text>
            <Text>Table No: {billInfo.Table_No}</Text>
            <Text>Customer: {billInfo.GuestName || "N/A"}</Text>
            <Text>PAN No: {billInfo.guestPan || "N/A"}</Text>
          </View>
          <Text style={[styles.smallText, styles.bold]}>Payment Mode: {billInfo.PaymentMode}</Text>
          <View style={styles.smallText}>
            <Text>Employee: {billInfo.employee}</Text>
            <Text>Transaction Date: {billInfo.Date}</Text>
            <Text>Invoice Issue Date: {billInfo.Date}</Text>
            <Text>Nepali Miti: {billInfo.Date ? adToBs(billInfo.Date) : "N/A"}</Text>
          </View>
        </View>

        <View style={styles.borderLine} />

        <View style={styles.table}>
          <View style={[styles.flexBetween, styles.tHeadBorderBottom]}>
            <Text style={[styles.th, styles.bold, { width: "10%" }]}>HS Code</Text>
            <Text style={[styles.th, styles.bold, { width: "50%" }]}>Item Name</Text>
            <Text style={[styles.th, styles.bold, { width: "15%" }]}>Rate</Text>
            <Text style={[styles.th, styles.bold, { width: "10%" }]}>Qty</Text>
            <Text style={[styles.th, styles.bold, { width: "15%" }]}>Total</Text>
          </View>

          {billInfoList.map((item, idx) => (
            <View key={idx} style={styles.flexBetween}>
              <Text style={[styles.td, { width: "10%" }]}>{item.HsCode}</Text>
              <Text style={[styles.td, { width: "50%" }]}>{item.itemName}</Text>
              <Text style={[styles.td, { width: "15%" }]}>{formatSalesValue(item.itemrate)}</Text>
              <Text style={[styles.td, { width: "10%" }]}>{item.Quantity}</Text>
              <Text style={[styles.td, { width: "15%" }]}>{formatSalesValue(item.total)}</Text>
            </View>
          ))}

          <View style={[styles.flexBetween, styles.tHeadBorderTop, { paddingTop: 1, fontWeight: 'bold' }]}>
            <Text style={{ width: "75%" }}></Text>
            <Text style={{ width: "10%" }}>
              {billInfoList.reduce((s, i) => s + Number(i.Quantity), 0)}
            </Text>
            <Text style={{ width: "15%" }}>{formatSalesValue(taxable_total)}</Text>
          </View>
        </View>

        <View style={{ alignItems: "flex-end", marginTop: 4 }}>
          <Text style={styles.smallText}>Discount: {formatSalesValue(billInfo.DiscountAmt)}</Text>
          <Text style={styles.smallText}>Taxable Amount: {formatSalesValue(taxable_total)}</Text>
          <Text style={styles.smallText}>Tax Amount (VAT): {formatSalesValue(billInfo.vat)}</Text>
          {round_off !== 0 && (
            <Text style={[styles.smallText, styles.bold, { fontSize: 9 }]}>
              Rounded Off: {round_off.toFixed(2)}
            </Text>
          )}
          <Text
            style={[
              styles.smallText,
              styles.bold,
              {
                textDecoration: "underline",
                textTransform: "uppercase",
                textDecorationThickness: "2px",
                marginTop: 4,
              },
            ]}
          >
            Grand Total: {formatSalesValue(billInfo.total)}
          </Text>
        </View>

        <Text style={[styles.smallText, { marginTop: 6, textAlign: "right" }]}>
          In Words: {toWords.convert(Number(billInfo.total) || 0)}
        </Text>

        <View style={styles.footerLine} />
      </Page>
    </Document>
  );
};

export default ExportableInvoicePDF;













// import {
//   Page,
//   Text,
//   View,
//   Document,
//   Image,
//   StyleSheet,
// } from "@react-pdf/renderer";
// import { ToWords } from "to-words";
// import { adToBs } from "@sbmdkl/nepali-date-converter";
// import { formatSalesValue } from "../../utils/formatUtils";

// const styles = StyleSheet.create({
//   page: { padding: 24, width: 700, fontFamily: "Helvetica", fontSize: 10, color: "#000" },
//   center: { textAlign: "center" },
//   headerLogo: { alignItems: "center", marginBottom: 4 },
//   logo: { height: 96 },
//   company: { fontSize: 16, fontWeight: "bold", textDecoration: "underline", textDecorationThickness: "2px" },
//   smallText: { fontSize: 10, marginTop: 7 },
//   sectionTitle: { fontSize: 10, fontWeight: "bold", textDecoration: "underline", marginTop: 6, marginBottom: 4 },
//   flexBetween: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", marginVertical: 5 },
//   borderLine: { borderBottomWidth: 2, borderTopWidth: 2, borderColor: "#000", height: 5, marginVertical: 2 },
//   table: { width: "100%", fontSize: 9 },
//   th: { paddingBottom: 2 },
//   td: {
//     padding: 0,
//     lineHeight: 0.2,
//   },
//   tHeadBorderTop: { borderTopWidth: 2, borderColor: "#000" },
//   tHeadBorderBottom: { borderBottomWidth: 2, borderColor: "#000" },
//   bold: { fontWeight: "bold" },
//   footerLine: { borderBottomWidth: 4, borderColor: "#000", marginTop: 1 },
// });

// const ExportableInvoicePDF = ({ billInfo, billInfoList, logoSrc }) => {
//   const address = localStorage.getItem("selectedAddress");
//   const company = localStorage.getItem("selectedCompany");
//   const pan_no = localStorage.getItem("selectedPan");
//   const phone = localStorage.getItem("selectedPhone");

//   const toWords = new ToWords({
//     localeCode: "en-IN",
//     converterOptions: {
//       currency: true,
//       ignoreDecimal: false,
//       ignoreZeroCurrency: false,
//       doNotAddOnly: false,
//       currencyOptions: {
//         name: "Rupee",
//         plural: "Rupees",
//         symbol: "₹",
//         fractionalUnit: {
//           name: "Paisa",
//           plural: "Paise",
//           symbol: "",
//         },
//       },
//     },
//   });

//   const taxable_total = billInfoList.reduce((s, i) => s + Number(i.total), 0);
//   const round_off = Number(billInfo.total) - Number(taxable_total) - Number(billInfo.vat) + Number(billInfo.DiscountAmt);

//   return (
//     <Document>
//       <Page size="A4" style={styles.page}>
//         <View style={styles.center}>
//           <View style={styles.headerLogo}>
//             {logoSrc && <Image src={logoSrc} style={styles.logo} />}
//           </View>
//           <Text style={styles.company}>{company}</Text>
//           <View style={styles.smallText}>
//             <Text>{address}</Text>
//             {pan_no && <Text>PAN No: {pan_no}</Text>}
//             {phone && <Text>Phone: {phone}</Text>}
//           </View>
//         </View>

//         <Text style={[styles.sectionTitle, styles.center]}>TAX INVOICE</Text>

//         <View style={styles.flexBetween}>
//           <View style={styles.smallText}>
//             <Text>Bill No: {billInfo.bill_no}</Text>
//             <Text>Table No: {billInfo.Table_No}</Text>
//             <Text>Customer: {billInfo.GuestName || "N/A"}</Text>
//             <Text>PAN No: {billInfo.guestPan || "N/A"}</Text>
//           </View>
//           <Text style={[styles.smallText, styles.bold]}>Payment Mode: {billInfo.PaymentMode}</Text>
//           <View style={styles.smallText}>
//             <Text>Employee: {billInfo.employee}</Text>
//             <Text>Transaction Date: {billInfo.Date}</Text>
//             <Text>Invoice Issue Date: {billInfo.Date}</Text>
//             <Text>Nepali Miti: {billInfo.Date ? adToBs(billInfo.Date) : "N/A"}</Text>
//           </View>
//         </View>

//         <View style={styles.borderLine} />

//         <View style={styles.table}>
//           <View style={[styles.flexBetween, styles.tHeadBorderBottom]}>
//             <Text style={[styles.th, styles.bold, { width: "10%" }]}>HS Code</Text>
//             <Text style={[styles.th, styles.bold, { width: "50%" }]}>Item Name</Text>
//             <Text style={[styles.th, styles.bold, { width: "15%" }]}>Rate</Text>
//             <Text style={[styles.th, styles.bold, { width: "10%" }]}>Qty</Text>
//             <Text style={[styles.th, styles.bold, { width: "15%" }]}>Total</Text>
//           </View>

//           {billInfoList.map((item, idx) => (
//             <View key={idx} style={styles.flexBetween}>
//               <Text style={[styles.td, { width: "10%" }]}>{item.HsCode}</Text>
//               <Text style={[styles.td, { width: "50%" }]}>{item.itemName}</Text>
//               <Text style={[styles.td, { width: "15%" }]}>{formatSalesValue(item.itemrate)}</Text>
//               <Text style={[styles.td, { width: "10%" }]}>{item.Quantity}</Text>
//               <Text style={[styles.td, { width: "15%" }]}>{formatSalesValue(item.total)}</Text>
//             </View>
//           ))}

//           <View style={[styles.flexBetween, styles.tHeadBorderTop, { paddingTop: 1, fontWeight: 'bold' }]}>
//             <Text style={{ width: "75%" }}></Text>
//             <Text style={{ width: "10%" }}>
//               {billInfoList.reduce((s, i) => s + Number(i.Quantity), 0)}
//             </Text>
//             <Text style={{ width: "15%" }}>{formatSalesValue(taxable_total)}</Text>
//           </View>
//         </View>

//         <View style={styles.smallText}>
//           <Text>Discount: {formatSalesValue(billInfo.DiscountAmt)}</Text>
//           <Text>Taxable Amount: {formatSalesValue(taxable_total)}</Text>
//           <Text>Tax Amount (VAT): {formatSalesValue(billInfo.vat)}</Text>
//           {round_off !== 0 && (
//             <Text style={[styles.bold, { fontSize: 9, marginTop: 4 }]}>Rounded Off: {round_off.toFixed(2)}</Text>
//           )}
//         </View>

//         <View style={[styles.flexBetween]}>
//           <Text style={[styles.bold, { textDecoration: "underline", textTransform: "uppercase", textDecorationThickness: "2px" }]}>
//             Grand Total: {formatSalesValue(billInfo.total)}
//           </Text>
//           <Text>In Words: {toWords.convert(Number(billInfo.total) || 0)}</Text>
//         </View>

//         <View style={styles.footerLine} />
//       </Page>
//     </Document>
//   );
// };

// export default ExportableInvoicePDF;
